import * as React from 'react';
import styled, { css } from 'styled-components';

import { AquaTheme as T } from '../AquaTheme';
import { AquaMapControl } from './AquaMapControl';

interface IAquaMapButtonProps {
  /** @ignore */
  className?: string;
  /** @ignore */
  children?: React.ReactNode;
  /** Horizontal position. A negative value is an offset from the right. Ignored when grouped. */
  x?: number;
  /** Vertical position. A negative value is an offset from the bottom. Ignored when grouped. */
  y?: number;
  /** Active buttons are drawn in the accent color. */
  active?: boolean;
  /** Disabled buttons cannot be clicked. */
  disabled?: boolean;
  /** Hint shown to the left of the button on hover. */
  hint?: React.ReactNode;
  /** Accessible label; defaults to the hint if it is a string. */
  ariaLabel?: string;
  /**
   * Set by AquaMapButtonGroup. A grouped button is not positioned itself.
   * @default false
   */
  grouped?: boolean;
  onClick: (e: React.MouseEvent | React.KeyboardEvent) => void;
}

/**
 * Square white map button. Port of AquaUI's MapButton; the hint is pure CSS
 * rather than a Popper portal, and always opens to the left.
 */
const AquaMapButtonBase = (props: IAquaMapButtonProps) => {
  const handleClick = (e: React.MouseEvent) => {
    if(props.disabled) return;
    props.onClick(e);
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if(props.disabled) return;
    if(e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      props.onClick(e);
    }
  }

  const button =
    <div
      className={props.className}
      tabIndex={props.disabled ? -1 : 0}
      role="button"
      aria-label={props.ariaLabel ?? (typeof props.hint === 'string' ? props.hint : undefined)}
      aria-disabled={props.disabled}
      aria-pressed={props.active}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <Face className="face">
        {props.children}
      </Face>
      {props.hint && <Hint>{props.hint}</Hint>}
    </div>

  if(props.grouped) return button;
  return <AquaMapControl x={props.x} y={props.y}>{button}</AquaMapControl>;
}

const Hint = styled.div`
  position: absolute;
  right: 100%;
  top: 50%;
  margin-right: 8px;
  height: 24px;
  padding: 0 12px;
  transform: translate(4px, -50%);
  border-radius: ${T.radius.normal}px;
  background-color: ${T.colors.neutral[10]};
  color: ${T.colors.neutral[100]};
  font: ${T.font.labelSmall};
  white-space: nowrap;
  display: flex;
  align-items: center;
  pointer-events: none;
  opacity: 0;
  transition: opacity ease-in-out ${T.duration * 3}ms, transform ease-out ${T.duration * 3}ms;
`

const Face = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 4px;
  background-color: ${T.colors.neutral[100]};
  transition: background-color ease-in-out ${T.duration}ms;
  svg {
    display: block;
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    padding: 8px;
    fill: ${T.colors.neutral[10]};
    transition: fill ease-in-out ${T.duration}ms;
  }
`

const AquaMapButtonStyled = styled(AquaMapButtonBase)`
  position: relative;
  width: 32px;
  height: 32px;
  outline: 0;
  cursor: pointer;
  user-select: none;
  box-shadow: ${p => p.grouped ? 'none' : T.shadows.small};
  border-radius: 4px;

  &:hover ${Face} {
    background-color: ${T.colors.neutral[95]};
  }
  &:hover ${Hint} {
    opacity: 1;
    transform: translate(0, -50%);
  }
  &:focus-visible {
    outline: solid 2px ${T.colors.primary[1]};
    outline-offset: 1px;
    z-index: 1;
  }

  ${p => p.active && css`
    ${Face}, &:hover ${Face} {
      background-color: ${T.colors.accent};
      svg { fill: ${T.colors.neutral[100]}; }
    }
  `}

  ${p => p.disabled && css`
    cursor: default;
    ${Face}, &:hover ${Face} {
      background-color: ${T.colors.neutral[80]};
      svg { fill: ${T.colors.neutral[50]}; }
    }
  `}
`

const AquaMapButton = ({ active = false, disabled = false, grouped = false, ...props }: IAquaMapButtonProps) =>
  <AquaMapButtonStyled active={active} disabled={disabled} grouped={grouped} {...props}/>

export { AquaMapButton, IAquaMapButtonProps }
